import React, { useCallback, useState } from 'react';
import { Collapse } from 'reactstrap';
import { BasicContainer, ButtonAsWrapper, FlexRow } from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { useTranslation } from '@blocs.i18n';

type ControlledProps = {
  /** whether the panel is toggled open (controlled component usage) */
  isOpen: boolean;
  /** callback handler for the toggle event when isOpen is set */
  onToggle: () => void;
  /** initialOpen cannot be used when isOpen is set */
  initialOpen?: never;
};

type UncontrolledProps = {
  /** isOpen cannot be used when initialOpen is set */
  isOpen?: never;
  /** onToggle cannot be used when initialOpen is set */
  onToggle?: never;
  /** set the initial value of the panel's state in uncontrolled mode
   * (this prop is ignored if isOpen is set)
   * defaults to true
   */
  initialOpen?: boolean;
};

type CollapsePanelStateProps = ControlledProps | UncontrolledProps;

export type CollapsePanelProps = CollapsePanelStateProps & {
  /** component to render that will toggle the panel open/closed when clicked, do not nest buttons in this component */
  togglerChildren: React.ReactNode;
  /** props to style the flex row container that the headerChildren are inside */
  togglerProps?: React.ComponentProps<typeof FlexRow>;
  /** component to render in the header, but outside the toggler button */
  headerContentChildren?: React.ReactNode;
  /** props to style the basic container that the headerContentChildren are inside */
  headerContentProps?: React.ComponentProps<typeof BasicContainer>;
  /** component to render inside the panel when it is open */
  bodyChildren: React.ReactNode;
  /** props to style the basic container that the bodyChildren are inside */
  bodyProps?: React.ComponentProps<typeof BasicContainer>;
  /** label for the button that wraps the toggle header, defaults to "Open" / "Close" */
  toggleLabel?: string;
  /** props for the icon that renders in the toggle header, can be used to set custom size, color, etc */
  toggleIconProps?: React.ComponentProps<typeof Icon>;
  /** controls whether the toggle icon is rendered in the header */
  toggleIconHidden?: boolean;
  /** qa id value for the wrapper around the entire collapse toggle & panel */
  'data-qa-id'?: string;
  /** testid value for the wrapper around the entire collapse toggle & panel */
  'data-testid'?: string;
};

export const CollapsePanel = ({
  'data-qa-id': qaId,
  'data-testid': testId,
  bodyChildren,
  bodyProps = {},
  headerContentChildren,
  headerContentProps = {},
  togglerChildren,
  togglerProps = {},
  initialOpen = true,
  isOpen: isOpenProp,
  onToggle,
  toggleIconHidden = false,
  toggleIconProps,
  toggleLabel: toggleLabelProp,
}: CollapsePanelProps) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(isOpenProp || initialOpen);
  const isOpenVal = typeof isOpenProp === 'boolean' ? isOpenProp : isOpen;
  const toggleLabel = toggleLabelProp || (isOpenVal ? t('common:button.close') : t('common:button.open'));
  const testIdBase = testId || 'collapse-panel';

  const toggle = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (typeof isOpenProp === 'boolean') {
        onToggle?.();

        return;
      }

      setOpen(!isOpen);
    },
    [isOpen, isOpenProp, onToggle]
  );

  return (
    <BasicContainer w="full" data-qa-id={qaId} data-testid={`${testIdBase}-wrapper`}>
      <BasicContainer w="full" data-testid={`${testIdBase}-header`}>
        <ButtonAsWrapper
          className="cn_w-full"
          aria-label={toggleLabel}
          aria-expanded={isOpenVal}
          onClick={toggle}
          unsetSizing
          noPadding
        >
          <FlexRow
            w="full"
            bgColor="card"
            textColor="default"
            borderPlacement="bottom"
            justifyContent="space-between"
            alignItems="flex-start"
            wrap="nowrap"
            {...togglerProps}
            data-testid={`${testIdBase}-toggler`}
          >
            {togglerChildren}
            {!toggleIconHidden && (
              <Icon
                size="sm"
                inheritColor
                name={isOpenVal ? 'chevron-open-thick' : 'chevron-closed-thick'}
                {...(toggleIconProps || {})}
              />
            )}
          </FlexRow>
        </ButtonAsWrapper>
        {headerContentChildren && (
          <BasicContainer
            w="full"
            bgColor="card"
            {...headerContentProps}
            data-testid={`${testIdBase}-header-content-wrapper`}
          >
            {headerContentChildren}
          </BasicContainer>
        )}
      </BasicContainer>
      <Collapse className="cn_w-full" data-testid={`${testIdBase}-body`} isOpen={isOpenVal}>
        <BasicContainer w="full" bgColor="card" {...bodyProps} data-testid={`${testIdBase}-body-wrapper`}>
          {bodyChildren}
        </BasicContainer>
      </Collapse>
    </BasicContainer>
  );
};
